import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    useMediaQuery,
    useTheme,
    tooltipClasses,
    TooltipProps,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    Autocomplete,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { Edit, Delete, Search, Sms, Add, Close } from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface TemplateVar {
    id?: number;
    var_name: string;
    var_order: number;
    var_sample_value: string;
}

interface SmsTemplate {
    id: number;
    template_id: string;
    name: string;
    content: string;
    is_global: boolean;
    variables: TemplateVar[];
    tenants: { id: number; name: string }[];
    created_at: string;
}

const SmsTemplates = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);
    const [templateId, setTemplateId] = useState('');
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [variables, setVariables] = useState<TemplateVar[]>([]);
    const [isGlobal, setIsGlobal] = useState(true);
    const [selectedTenantIds, setSelectedTenantIds] = useState<{ id: number; name: string }[]>([]);
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<SmsTemplate | null>(null);

    // Fetch templates
    const { data, isLoading } = useQuery({
        queryKey: ['sms-templates', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const response = await api.get('/superadmin/sms-templates', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    // Fetch available var names from helper
    const { data: availableVarsData } = useQuery({
        queryKey: ['sms-template-available-vars'],
        queryFn: async () => {
            const response = await api.get('/superadmin/sms-templates/available-vars');
            return response.data;
        },
    });

    const availableVars: string[] = availableVarsData?.data || [];

    // Fetch tenants for multi-select
    const { data: tenantsData } = useQuery({
        queryKey: ['tenants-list'],
        queryFn: async () => {
            const res = await api.get('/superadmin/tenants', { params: { per_page: 500 } });
            return res.data?.data?.data || res.data?.data || [];
        },
    });
    const allTenants: { id: number; name: string }[] = Array.isArray(tenantsData) ? tenantsData : [];

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (payload: { template_id: string; name: string; content: string; is_global: boolean; tenant_ids: number[]; variables: TemplateVar[] }) => {
            if (selectedTemplate) {
                return api.put(`/superadmin/sms-templates/${selectedTemplate.id}`, payload);
            }
            return api.post('/superadmin/sms-templates', payload);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['sms-templates'] });
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.delete(`/superadmin/sms-templates/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sms-templates'] });
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    const handleOpen = (template?: SmsTemplate) => {
        if (template) {
            setSelectedTemplate(template);
            setTemplateId(template.template_id);
            setName(template.name);
            setContent(template.content);
            setIsGlobal(template.is_global);
            setSelectedTenantIds(template.tenants || []);
            setVariables(template.variables.map((v) => ({
                var_name: v.var_name,
                var_order: v.var_order,
                var_sample_value: v.var_sample_value || '',
            })));
        } else {
            setSelectedTemplate(null);
            setTemplateId('');
            setName('');
            setContent('');
            setIsGlobal(true);
            setSelectedTenantIds([]);
            setVariables([]);
        }
        setError('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedTemplate(null);
        setTemplateId('');
        setName('');
        setContent('');
        setIsGlobal(true);
        setSelectedTenantIds([]);
        setVariables([]);
        setError('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate({
            template_id: templateId,
            name,
            content,
            is_global: isGlobal,
            tenant_ids: isGlobal ? [] : selectedTenantIds.map(t => t.id),
            variables,
        });
    };

    const handleDeleteClick = (template: SmsTemplate) => {
        setTemplateToDelete(template);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (templateToDelete) {
            deleteMutation.mutate(templateToDelete.id);
        }
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
    };

    // Variable management
    const addVariable = () => {
        setVariables([...variables, { var_name: '', var_order: variables.length, var_sample_value: '' }]);
    };

    const removeVariable = (index: number) => {
        const updated = variables.filter((_, i) => i !== index).map((v, i) => ({ ...v, var_order: i }));
        setVariables(updated);
    };

    const updateVariable = (index: number, field: keyof TemplateVar, value: string | number) => {
        const updated = [...variables];
        updated[index] = { ...updated[index], [field]: value };
        setVariables(updated);
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'template_id', headerName: 'Template ID', width: 150 },
        { field: 'name', headerName: 'Template Name', flex: 1, minWidth: 180 },
        {
            field: 'content',
            headerName: 'Content',
            flex: 1,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => (
                <BootstrapTooltip title={params.value} placement="top">
                    <Typography variant="body2" noWrap>{params.value}</Typography>
                </BootstrapTooltip>
            ),
        },
        {
            field: 'variables',
            headerName: 'Variables',
            width: 200,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
                    {(params.value || []).map((v: TemplateVar, i: number) => (
                        <Chip key={i} label={v.var_name} size="small" sx={{ fontSize: 11 }} />
                    ))}
                </Box>
            ),
        },
        {
            field: 'is_global',
            headerName: 'Availability',
            width: 180,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                if (params.value) {
                    return <Chip label="All Tenants" size="small" color="success" sx={{ fontWeight: 600, borderRadius: '6px' }} />;
                }
                const tenants = params.row.tenants || [];
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
                        {tenants.length === 0 ? (
                            <Chip label="No Tenants" size="small" color="warning" sx={{ fontWeight: 600, borderRadius: '6px' }} />
                        ) : tenants.map((t: any) => (
                            <Chip key={t.id} label={t.name} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        ))}
                    </Box>
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Created',
            width: 120,
            valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    const mobileColumns: GridColDef[] = [
        { field: 'name', headerName: 'Template Name', flex: 1 },
        { field: 'template_id', headerName: 'Template ID', width: 120 },
        {
            field: 'actions',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton size="small" onClick={() => handleOpen(params.row)}>
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const rows = data?.data?.data || data?.data || [];
    const rowCount = data?.data?.total || data?.total || 0;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h2">SMS Templates</Typography>
            </Box>

            {/* Search & Add */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        sx={{ width: '30%', minWidth: 200, borderRadius: 1 }}
                        size="small"
                        placeholder="Search templates..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" startIcon={<Sms />} onClick={() => handleOpen()}>
                        Add Template
                    </Button>
                </CardContent>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* DataGrid */}
            <Card>
                <DataGrid
                    rows={rows}
                    columns={isMobile ? mobileColumns : columns}
                    loading={isLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50]}
                    paginationMode="server"
                    rowCount={rowCount}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                    getRowHeight={() => 'auto'}
                />
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedTemplate ? 'Edit Template' : 'Add Template'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {error && <Alert severity="error">{error}</Alert>}

                        <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                            <TextField
                                label="Template ID"
                                fullWidth
                                required
                                value={templateId}
                                onChange={(e) => setTemplateId(e.target.value)}
                                placeholder="e.g. 12345"
                            />
                            <TextField
                                label="Template Name"
                                fullWidth
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Birthday Greeting"
                            />
                        </Box>

                        <TextField
                            label="Content"
                            fullWidth
                            required
                            multiline
                            rows={4}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Hello {name}, your voter ID is {voter_id}..."
                            helperText="Use {var_name} placeholders for dynamic content"
                        />

                        {/* Tenant Assignment */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isGlobal}
                                        onChange={(e) => {
                                            setIsGlobal(e.target.checked);
                                            if (e.target.checked) setSelectedTenantIds([]);
                                        }}
                                    />
                                }
                                label="Available to All Tenants"
                            />
                            {!isGlobal && (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={allTenants}
                                    getOptionLabel={(o) => o.name}
                                    value={selectedTenantIds}
                                    onChange={(_, v) => setSelectedTenantIds(v)}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    renderInput={(params) => <TextField {...params} label="Select Tenants" placeholder="Search tenants..." />}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                                        ))
                                    }
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>

                        {/* Variables Section */}
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>Template Variables</Typography>
                                <Button size="small" startIcon={<Add />} onClick={addVariable} variant="outlined">
                                    Add Variable
                                </Button>
                            </Box>

                            {variables.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                    No variables added. Click "Add Variable" to map dynamic placeholders.
                                </Typography>
                            )}

                            {variables.map((v, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="body2" sx={{ minWidth: 24, fontWeight: 600, color: 'text.secondary' }}>
                                        {index + 1}.
                                    </Typography>
                                    <FormControl size="small" sx={{ minWidth: 180 }} required>
                                        <InputLabel>Variable Name</InputLabel>
                                        <Select
                                            value={v.var_name}
                                            label="Variable Name"
                                            onChange={(e: SelectChangeEvent) => updateVariable(index, 'var_name', e.target.value)}
                                        >
                                            {availableVars.map((varName) => (
                                                <MenuItem key={varName} value={varName}>{varName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        label="Sample Value"
                                        value={v.var_sample_value}
                                        onChange={(e) => updateVariable(index, 'var_sample_value', e.target.value)}
                                        placeholder="e.g. John"
                                        sx={{ flex: 1 }}
                                    />
                                    <IconButton size="small" color="error" onClick={() => removeVariable(index)}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
            >
                <Box sx={{
                    bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2,
                    borderBottom: '1px solid #fecaca'
                }}>
                    <Box sx={{
                        bgcolor: '#dc2626', borderRadius: '50%', p: 1.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete template <strong>"{templateToDelete?.name}"</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{
                        bgcolor: '#fef3c7', border: '1px solid #fcd34d',
                        '& .MuiAlert-icon': { color: '#d97706' }
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Warning: You will not be able to revert this action!
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={handleDeleteCancel}
                        sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}
                        disabled={deleteMutation.isPending} startIcon={<Delete />}
                        sx={{
                            borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600,
                            bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }
                        }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SmsTemplates;
